<template>
  <div
    ref="widgetContainer"
    :class="[props.mode === 'widget' ? 'szw-widget-container' : 'szw-component-container', 'szw-core szw-gap-2']"
    e2e-id="szw-scoreboard-widget"
    :theme-id="activeTheme"
    :lang-id="languageIdentifier"
    :home-team-id="props.competitorId"
    :away-team-id="props.competitor2Id"
    :event-id="props.eventId"
    :drf-id="widgetId"
  >
    <fallback-screen-handler>
      <!-- HEADER -->
      <common-header v-if="props.mode === 'widget'" :initial-data="scoreboardStore.initial" :is-small-widget-container-size="isSmallWidgetContainerSize" :mode="'no-underline'" />

      <div class="szw-flex szw-flex-col szw-h-full szw-w-full szw-max-w-[768px] szw-mx-auto szw-gap-2">
        <!-- VENUE -->
        <venue-banner v-if="scoreboardStore.getVenueDetails" :venue-details="scoreboardStore.getVenueDetails" />

        <!-- FORM -->
        <form-banner
          v-if="isFormVisibleMemo"
          :home-team-events="homeTeamEvents"
          :away-team-events="awayTeamEvents"
          :is-small-widget-container-size="isSmallWidgetContainerSize"
          :scoreboard-form-label="scoreboardStore.getForm.label"
          :events-number-label="loc(`sm_last_${eventsNumber}_games`, languageIdentifier)"
          :events-number="eventsNumber"
        />

        <previous-matches-banner
          :home-wins="scoreboardStore.scoreboard.previousMatches.homeWins"
          :away-wins="scoreboardStore.scoreboard.previousMatches.awayWins"
          :draws="scoreboardStore.scoreboard.previousMatches.draws"
          :label="scoreboardStore.scoreboard.previousMatches.label"
        />
      </div>
    </fallback-screen-handler>
  </div>
</template>

<script setup>
import FallbackScreenHandler from '@/__core__/components/fallbackScreens/FallbackScreenHandler.vue'
import { ref, computed, provide } from 'vue'
import useReactors from '@/__core__/composables/useReactors'
import useSize from '@/__core__/composables/useSize'
import CommonHeader from '@/__core__/components/headers/common-header/CommonHeader.vue'
import VenueBanner from './components/VenueBanner.vue'
import FormBanner from './components/FormBanner.vue'
import PreviousMatchesBanner from './components/PreviousMatchesBanner.vue'
import useFetch from '@/__core__/composables/useFetch'
import { useScoreboardStore } from './Scoreboard.store'
import '@/__core__/style/style.scss'
import { useDataRefresh } from '@/__core__/data-refresh/useDataRefresh'

const widgetId = 'scoreboard'

/** @type {{ mode: 'widget' | 'component' }} */
const props = defineProps({
  languageIdentifier: {
    type: String,
    default: () => 'eng'
  },
  eventId: {
    type: String,
    default: 'sr:sport_event:41763601'
  },
  competitorId: {
    type: String,
    default: () => 'sr:competitor:37'
  },
  competitor2Id: {
    type: String,
    default: () => 'sr:competitor:44'
  },
  region: {
    type: String,
    default: () => ''
  },
  sportType: {
    type: String,
    default: () => 'football'
  },
  theme: {
    type: String,
    default: () => 'light'
  },
  size: {
    type: Number,
    default: 640
  },
  mode: {
    type: String,
    default: 'widget'
  },
  isWidgetInjected: {
    type: Boolean
  },
  themeConfig: {
    type: Object,
    default: () => null
  }
})

const eventsNumber = ref(3)

/** @type Ref<HTMLElement> */
const widgetContainer = ref(null)

const scoreboardStore = useScoreboardStore()
const { startRefresh } = useDataRefresh(5 * 60 * 1000, widgetId)

const { activeTheme, activeSize } = useReactors(props.theme, props.size, { id: 'Scoreboard', shouldReset: !props.isWidgetInjected }, widgetContainer, undefined, props.themeConfig)
const { width: widgetContainerWidth } = useSize(
  widgetContainer,
  activeSize,
  width => {
    if (width.value <= 480) {
      eventsNumber.value = 3
    } else {
      eventsNumber.value = 5
    }
  },
  !props.isWidgetInjected
)

const isSmallWidgetContainerSize = computed(() => widgetContainerWidth.value <= 480)

const { status } = useFetch(async () => {
  const payload = {
    eventId: props.eventId,
    sportType: props.sportType,
    region: props.region,
    competitorId: props.competitorId,
    competitor2Id: props.competitor2Id,
    languageIdentifier: props.languageIdentifier
  }

  await scoreboardStore.fetchScoreboardData(payload)

  const refreshFn = async () => scoreboardStore.scheduleScoreboardRefresh(payload)
  const shouldCallApi = () => scoreboardStore.getShouldCallApi()
  const shouldStopRefresh = () => scoreboardStore.getShouldStopRefresh
  startRefresh(refreshFn, shouldCallApi, shouldStopRefresh)
})

provide('status', status)
provide('langId', props.languageIdentifier)

const homeTeamEvents = computed(() => scoreboardStore.getTeamEvents('home', eventsNumber.value))
const awayTeamEvents = computed(() => scoreboardStore.getTeamEvents('away', eventsNumber.value))

const isFormVisibleMemo = computed(() => status.value === 'loading' || homeTeamEvents.value.length > 0 || awayTeamEvents.value.length > 0)
</script>
